import React, { useEffect, useState } from 'react';
import AutocompleteInput from './AutocompleteInput';
import { Certif } from '../../CertificatesPage';
import './CertificatesFilter.css';

interface CertificatesFilterProps {
  certificates: Certif[];
  onFilterChange: (filters: {
    cert_reg_number: string[];
    os_name: string[];
    os_number: string[];
    applicant_shortname: string[];
    manufacture_inn: string[];
    cert_reg_data: string[];
    manufacture_fullname: string[];
    applicant_inn: string[];
    applicant_region: string[];
    region_manufacturer: string[];
    lab_names: string[];
  }) => void;
  chartFilters: {
    aptsComments: string[];
    certStatuses: string[];
    years: string[];
  };
  onChartFilterChange: (filters: {
    aptsComments: string[];
    certStatuses: string[];
    years: string[];
  }) => void;
  hideApplicantFields?: boolean;
  onDateRangeChange?: (field: 'cert_reg_data' | 'cert_finish_data', startDate: string, endDate: string) => void;
  onTerminatedFilterChange?: (showTerminatedOnly: boolean) => void;
  initialFilters?: Partial<{
    cert_reg_number: string[];
    os_name: string[];
    os_number: string[];
    applicant_shortname: string[];
    manufacture_inn: string[];
    cert_reg_data: string[];
    manufacture_fullname: string[];
    applicant_inn: string[];
    applicant_region: string[];
    region_manufacturer: string[];
    lab_names: string[];
  }>;
  initialDateRanges?: Partial<{
    cert_reg_data: { start: string; end: string };
    cert_finish_data: { start: string; end: string };
  }>;
}

const CertificatesFilter: React.FC<CertificatesFilterProps> = ({ certificates, onFilterChange, chartFilters, onChartFilterChange, hideApplicantFields = false, onDateRangeChange, onTerminatedFilterChange, initialFilters, initialDateRanges }) => {
  const [filters, setFilters] = useState({
    cert_reg_number: [] as string[],
    os_name: [] as string[],
    os_number: [] as string[],
    applicant_shortname: [] as string[],
    manufacture_inn: [] as string[],
    cert_reg_data: [] as string[],
    manufacture_fullname: [] as string[],
    applicant_inn: [] as string[],
    applicant_region: [] as string[],
    region_manufacturer: [] as string[],
    lab_names: [] as string[]
  });

  const [inputValues, setInputValues] = useState({
    cert_reg_number: "",
    os_name: "",
    os_number: "",
    applicant_shortname: "",
    manufacture_inn: "",
    cert_reg_data: "",
    manufacture_fullname: "",
    applicant_inn: "",
    applicant_region: "",
    region_manufacturer: "",
    lab_names: ""
  });

  const [uniqueValues, setUniqueValues] = useState({
    cert_reg_number: new Set<string>(),
    os_name: new Set<string>(),
    os_number: new Set<string>(),
    applicant_shortname: new Set<string>(),
    manufacture_inn: new Set<string>(),
    cert_reg_data: new Set<string>(),
    manufacture_fullname: new Set<string>(),
    applicant_inn: new Set<string>(),
    applicant_region: new Set<string>(),
    region_manufacturer: new Set<string>(),
    lab_names: new Set<string>()
  });

  // Map formatted date (dd.mm.yy) -> original date string
  const [dateFormattedToOriginal, setDateFormattedToOriginal] = useState<Record<string, string>>({});

  // Состояния для фильтров по датам
  const [dateRanges, setDateRanges] = useState<{
    cert_reg_data: { start: string; end: string };
    cert_finish_data: { start: string; end: string };
  }>({
    cert_reg_data: { start: '', end: '' },
    cert_finish_data: { start: '', end: '' }
  });

  // Состояние для фильтра прекращенных сертификатов
  const [showTerminatedOnly, setShowTerminatedOnly] = useState(false);

  useEffect(() => {
    if (initialFilters) {
      const ensureArray = (v: any) => (Array.isArray(v) ? v : []);
      setFilters(prev => ({
        cert_reg_number: ensureArray(initialFilters.cert_reg_number),
        os_name: ensureArray(initialFilters.os_name),
        os_number: ensureArray(initialFilters.os_number),
        applicant_shortname: ensureArray(initialFilters.applicant_shortname),
        manufacture_inn: ensureArray(initialFilters.manufacture_inn),
        cert_reg_data: ensureArray(initialFilters.cert_reg_data),
        manufacture_fullname: ensureArray(initialFilters.manufacture_fullname),
        applicant_inn: ensureArray(initialFilters.applicant_inn),
        applicant_region: ensureArray(initialFilters.applicant_region),
        region_manufacturer: ensureArray(initialFilters.region_manufacturer),
        lab_names: ensureArray(initialFilters.lab_names),
      }));
    }
  }, [initialFilters]);

  useEffect(() => {
    if (initialDateRanges) {
      setDateRanges(prev => ({
        cert_reg_data: initialDateRanges.cert_reg_data || prev.cert_reg_data,
        cert_finish_data: initialDateRanges.cert_finish_data || prev.cert_finish_data,
      }));
      if (initialDateRanges.cert_reg_data && onDateRangeChange) {
        onDateRangeChange('cert_reg_data', initialDateRanges.cert_reg_data.start, initialDateRanges.cert_reg_data.end);
      }
      if (initialDateRanges.cert_finish_data && onDateRangeChange) {
        onDateRangeChange('cert_finish_data', initialDateRanges.cert_finish_data.start, initialDateRanges.cert_finish_data.end);
      }
    }
  }, [initialDateRanges, onDateRangeChange]);

  const formatDateDdMmYy = (isoOrAny: string): string => {
    if (!isoOrAny) return '';
    // Try parse ISO yyyy-mm-dd first
    const isoMatch = isoOrAny.match(/^(\d{4})[-/.](\d{2})[-/.](\d{2})/);
    let y: string, m: string, d: string;
    if (isoMatch) {
      y = isoMatch[1]; m = isoMatch[2]; d = isoMatch[3];
    } else {
      // Fallback to Date parsing
      const dt = new Date(isoOrAny);
      if (!isNaN(dt.getTime())) {
        y = String(dt.getFullYear());
        m = String(dt.getMonth() + 1).padStart(2, '0');
        d = String(dt.getDate()).padStart(2, '0');
      } else {
        return isoOrAny;
      }
    }
    const yy = y.slice(-2);
    return `${d}.${m}.${yy}`;
  };

  useEffect(() => {
    const newUniqueValues = {
      cert_reg_number: new Set<string>(),
      os_name: new Set<string>(),
      os_number: new Set<string>(),
      applicant_shortname: new Set<string>(),
      manufacture_inn: new Set<string>(),
      cert_reg_data: new Set<string>(),
      manufacture_fullname: new Set<string>(),
      applicant_inn: new Set<string>(),
      applicant_region: new Set<string>(),
      region_manufacturer: new Set<string>(),
      lab_names: new Set<string>()
    };
    const dateMap: Record<string, string> = {};

    certificates.forEach(cert => {
      (Object.keys(newUniqueValues) as Array<keyof typeof newUniqueValues>).forEach((key) => {
        const value = cert[key as keyof Certif];
        if (!value) return;
        const asString = value.toString();
        if (key === 'cert_reg_data') {
          const formatted = formatDateDdMmYy(asString);
          newUniqueValues.cert_reg_data.add(formatted);
          if (!(formatted in dateMap)) dateMap[formatted] = asString;
        } else {
          newUniqueValues[key].add(asString);
        }
      });

      const labFields = ['lan_name_1', 'lan_name_2', 'lan_name_3'];
      labFields.forEach(field => {
        const labName = (cert[field as keyof Certif] || '').toString().trim();
        if (labName && labName.toLowerCase() !== 'отсутствует') {
          newUniqueValues.lab_names.add(labName);
        }
      });
    });

    setUniqueValues(newUniqueValues);
    setDateFormattedToOriginal(dateMap);
  }, [certificates]);

  const handleInputChange = (field: keyof typeof inputValues, value: string) => {
    setInputValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddFilter = (field: keyof typeof filters, value: string) => {
    if (value && !filters[field].includes(value)) {
      const normalizedValue = field === 'cert_reg_data' && dateFormattedToOriginal[value]
        ? dateFormattedToOriginal[value]
        : value;
      const newFilters = {
        ...filters,
        [field]: [...filters[field], normalizedValue]
      };
      setFilters(newFilters);
      setInputValues(prev => ({
        ...prev,
        [field]: ""
      }));
      onFilterChange(newFilters);
    }
  };

  const handleSelect = (field: keyof typeof filters, e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const options = Array.from(field === 'lab_names' ? uniqueValues.lab_names : uniqueValues[field]);
    
    if (options.includes(value)) {
      handleAddFilter(field, value);
    }
  };

  const handleKeyDown = (field: keyof typeof filters, e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && inputValues[field]) {
      handleAddFilter(field, inputValues[field]);
    }
  };

  const handleRemoveFilter = (field: keyof typeof filters, value: string) => {
    const newFilters = {
      ...filters,
      [field]: filters[field].filter(v => v !== value)
    };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const resetFilters = () => {
    const emptyFilters = {
      cert_reg_number: [] as string[],
      os_name: [] as string[],
      os_number: [] as string[],
      applicant_shortname: [] as string[],
      manufacture_inn: [] as string[],
      cert_reg_data: [] as string[],
      manufacture_fullname: [] as string[],
      applicant_inn: [] as string[],
      applicant_region: [] as string[],
      region_manufacturer: [] as string[],
      lab_names: [] as string[]
    };
    setFilters(emptyFilters);
    setInputValues({
      cert_reg_number: "",
      os_name: "",
      os_number: "",
      applicant_shortname: "",
      manufacture_inn: "",
      cert_reg_data: "",
      manufacture_fullname: "",
      applicant_inn: "",
      applicant_region: "",
      region_manufacturer: "",
      lab_names: ""
    });
    onFilterChange(emptyFilters);
    
    onChartFilterChange({
      aptsComments: [],
      certStatuses: [],
      years: []
    });
  };

  const handleChartFilterRemove = (type: 'aptsComments' | 'certStatuses' | 'years', value: string) => {
    const newFilters = {
      ...chartFilters,
      [type]: chartFilters[type].filter(item => item !== value)
    };
    onChartFilterChange(newFilters);
  };

  const activeFiltersCount = Object.values(filters).reduce((acc, values) => acc + values.length, 0) + 
    chartFilters.aptsComments.length + 
    chartFilters.certStatuses.length + 
    chartFilters.years.length;

  const getFilterLabel = (field: keyof typeof filters) => {
    switch (field) {
      case 'cert_reg_number':
        return 'Номер';
      case 'os_name':
        return 'Название';
      case 'os_number':
        return 'Номер';
      case 'applicant_shortname':
        return 'Название';
      case 'manufacture_inn':
        return 'ИНН';
      case 'cert_reg_data':
        return 'Год';
      case 'manufacture_fullname':
        return 'Название';
      case 'applicant_inn':
        return 'ИНН';
      case 'applicant_region':
        return 'Регион';
      case 'region_manufacturer':
        return 'Регион';
      default:
        return '';
    }
  };

  const renderMultiSelect = (name: keyof typeof filters, label: string) => {
    const options = Array.from(uniqueValues[name]).sort();
    return (
      <div className="filter-field">
        <label htmlFor={name}>{label}</label>
        <div className="multi-select-container">
          <AutocompleteInput
            id={String(name)}
            value={inputValues[name]}
            options={options}
            selectedValues={filters[name]}
            placeholder={`Выберите (${options.length})`}
            onChange={(val) => handleInputChange(name, val)}
            onSelectOption={(val) => handleAddFilter(name, val)}
          />
        </div>
      </div>
    );
  };

  // Функции для работы с диапазонами дат
  const getDateRange = (field: 'cert_reg_data' | 'cert_finish_data') => {
    const dates = certificates
      .map(item => item[field])
      .filter(date => date)
      .map(date => new Date(date))
      .filter(date => !isNaN(date.getTime()));

    if (dates.length === 0) {
      const today = new Date();
      return {
        minDate: new Date(today.getFullYear() - 5, 0, 1),
        maxDate: today
      };
    }

    const oldestDate = new Date(Math.min(...dates.map(d => d.getTime())));
    const newestDate = new Date(Math.max(...dates.map(d => d.getTime())));

    return {
      minDate: oldestDate,
      maxDate: newestDate
    };
  };

  const formatDateForInput = (date: Date): string => {
    return date.toISOString().split('T')[0];
  };

  const handleDateRangeChange = (field: 'cert_reg_data' | 'cert_finish_data', startDate: string, endDate: string) => {
    setDateRanges(prev => ({
      ...prev,
      [field]: { start: startDate, end: endDate }
    }));
    
    if (onDateRangeChange) {
      onDateRangeChange(field, startDate, endDate);
    }
  };

  const clearDateRange = (field: 'cert_reg_data' | 'cert_finish_data') => {
    const { minDate, maxDate } = getDateRange(field);
    handleDateRangeChange(field, formatDateForInput(minDate), formatDateForInput(maxDate));
  };

  const handleTerminatedFilterChange = (checked: boolean) => {
    setShowTerminatedOnly(checked);
    if (onTerminatedFilterChange) {
      onTerminatedFilterChange(checked);
    }
  };

  const renderDateRangeFilter = (field: 'cert_reg_data' | 'cert_finish_data', label: string) => {
    const { minDate, maxDate } = getDateRange(field);
    const currentRange = dateRanges[field];

    return (
      <div className="date-range-filter">
        <h5>{label}</h5>
        <div className="date-inputs">
          <div className="date-input-group">
            <label htmlFor={`${field}-start`}>Начало периода:</label>
            <input
              id={`${field}-start`}
              type="date"
              value={currentRange.start}
              onChange={(e) => handleDateRangeChange(field, e.target.value, currentRange.end)}
              min={formatDateForInput(minDate)}
              max={formatDateForInput(maxDate)}
              className="date-input"
            />
          </div>
          <div className="date-input-group">
            <label htmlFor={`${field}-end`}>Конец периода:</label>
            <input
              id={`${field}-end`}
              type="date"
              value={currentRange.end}
              onChange={(e) => handleDateRangeChange(field, currentRange.start, e.target.value)}
              min={formatDateForInput(minDate)}
              max={formatDateForInput(maxDate)}
              className="date-input"
            />
          </div>
          <button 
            type="button"
            onClick={() => clearDateRange(field)}
            className="clear-dates-btn"
            title="Очистить даты"
          >
            Сбросить
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="certificates-filter">
      <div className="filter-header">
        <h3>Фильтры {activeFiltersCount > 0 && <span className="filter-count">({activeFiltersCount})</span>}</h3>
        <button className="reset-button" onClick={resetFilters}>Сбросить</button>
      </div>

      {(Object.values(filters).some(values => values.length > 0) || 
        chartFilters.aptsComments.length > 0 || 
        chartFilters.certStatuses.length > 0 || 
        chartFilters.years.length > 0) && (
        <div className="active-filters">
          <h4>Активные фильтры:</h4>
          {Object.entries(filters).map(([field, values]) => {
            return values.map(value => (
              <div key={`${field}-${value}`} className="active-filter-item">
                <span data-label={getFilterLabel(field as keyof typeof filters)}>{
                  (field === 'cert_reg_data') ? formatDateDdMmYy(value) : value
                }</span>
                <button 
                  className="remove-filter-button"
                  onClick={() => handleRemoveFilter(field as keyof typeof filters, value)}
                >
                  ×
                </button>
              </div>
            ));
          })}
          {chartFilters.aptsComments.map(comment => (
            <div key={comment} className="active-filter-item">
              <span data-label="Комментарий">Комментарий АПТС: {comment}</span>
              <button 
                className="remove-filter-button"
                onClick={() => handleChartFilterRemove('aptsComments', comment)}
              >
                ×
              </button>
            </div>
          ))}
          {chartFilters.certStatuses.map(status => (
            <div key={status} className="active-filter-item">
              <span data-label="Статус">Статус: {status}</span>
              <button 
                className="remove-filter-button"
                onClick={() => handleChartFilterRemove('certStatuses', status)}
              >
                ×
              </button>
            </div>
          ))}
          {chartFilters.years.map(year => (
            <div key={year} className="active-filter-item">
              <span data-label="Год">Год: {year}</span>
              <button 
                className="remove-filter-button"
                onClick={() => handleChartFilterRemove('years', year)}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="filter-fields">
        <div className="filter-group2">
          <h4>Сертификат</h4>
          {renderMultiSelect('cert_reg_number', 'Номер сертификата')}
        </div>
        
        <div className="filter-group2">
          <h4>Орган по сертификации</h4>
          {renderMultiSelect('os_name', 'Название ОС')}
          {renderMultiSelect('os_number', 'Номер ОС')}
        </div>
        
        {!hideApplicantFields && (
          <div className="filter-group2">
            <h4>Заявитель</h4>
            {renderMultiSelect('applicant_shortname', 'Краткое название')}
            {renderMultiSelect('applicant_inn', 'ИНН')}
            {renderMultiSelect('applicant_region', 'Регион')}
          </div>
        )}
        
        <div className="filter-group2">
          <h4>Изготовитель</h4>
          {renderMultiSelect('manufacture_fullname', 'Полное название')}
          {renderMultiSelect('manufacture_inn', 'ИНН')}
          {renderMultiSelect('region_manufacturer', 'Регион')}
        </div>
        
        <div className="filter-group2">
          <h4>Лаборатории</h4>
          {renderMultiSelect('lab_names', 'Название лаборатории')}
        </div>
        
        <div className="filter-group2">
          <h4>Даты</h4>
          {renderDateRangeFilter('cert_reg_data', 'Дата регистрации сертификата')}
          {renderDateRangeFilter('cert_finish_data', 'Дата окончания сертификата')}
          
          {/* Фильтр прекращенных сертификатов */}
          <div className="terminated-filter-section">
            <button 
              type="button"
              onClick={() => handleTerminatedFilterChange(!showTerminatedOnly)}
              className={`terminated-filter-btn ${showTerminatedOnly ? 'active' : ''}`}
              title={showTerminatedOnly ? 'Показать все сертификаты' : 'Показать только прекращенные'}
            >
              {showTerminatedOnly ? 'Все сертификаты' : 'Только прекращенные'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CertificatesFilter; 